import { Typography } from '@mui/material';
import { NavLink } from 'react-router-dom';
import { Spacing } from '../../design-system/spacing';
import { Kind } from '../../types/kind';
import { styled } from '../../utils/styled';
import { Box } from '../Box';
import {
  SIDE_NAV_CONTENT_FADE_DELAY_MS,
  SIDE_NAV_CONTENT_FADE_DURATION_MS,
  SIDE_NAV_EASING,
  SIDE_NAV_EXPAND_DURATION_MS,
} from '../SideNavbar/styles';
import { Stack } from '../Stack';

export const ItemWrapper = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>`
  position: relative;
  box-sizing: border-box;
  align-items: center;
  color: ${({ color }) => color};
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
  height: 40px;
  padding: ${({ theme }) => theme.spacing(Spacing.Small)};
`;

export const StyledBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'color' &&
    prop !== 'expanded' &&
    prop !== 'primaryColor' &&
    prop !== 'secondaryColor',
})<{
  color: string;
  expanded: boolean;
  primaryColor: string;
  secondaryColor: string;
}>`
  color: ${({ color }) => color};
  --nav-icon-primary: ${({ primaryColor }) => primaryColor};
  --nav-icon-secondary: ${({ secondaryColor }) => secondaryColor};
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  & svg {
    display: block;
  }
  ${({ theme, expanded }) =>
    expanded
      ? `margin-right: ${theme.spacing(Spacing.Small)};`
      : `
    margin-right: 0;
    transition: margin-right 0s linear ${SIDE_NAV_CONTENT_FADE_DURATION_MS}ms;
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const StyledNavItemLabel = styled(Typography)`
  margin: 0;
  line-height: 21px;
`;

export const LabelContainer = styled('span', {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>`
  overflow: hidden;
  white-space: nowrap;
  position: relative;
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 320px;
  ${({ expanded }) =>
    expanded
      ? `
    transition: opacity ${SIDE_NAV_CONTENT_FADE_DURATION_MS}ms ${SIDE_NAV_EASING} ${SIDE_NAV_CONTENT_FADE_DELAY_MS}ms;
    opacity: 1;
    visibility: visible;
  `
      : `
    transition: opacity ${SIDE_NAV_CONTENT_FADE_DURATION_MS}ms ${SIDE_NAV_EASING}, visibility 0s linear ${SIDE_NAV_CONTENT_FADE_DURATION_MS}ms;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
export const NavItemWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'kind' && prop !== 'bigMargins',
})<{ kind: Kind; bigMargins?: boolean }>`
  white-space: nowrap;
  background-color: ${({ theme, kind }) =>
    kind === 'dark'
      ? theme.palette.background.dark
      : theme.palette.background.paper};
  margin: 0;
  margin-bottom: ${({ theme, bigMargins }) =>
    theme.spacing(bigMargins ? Spacing.Large : Spacing.Small)};
  margin-top: ${({ theme, bigMargins }) =>
    theme.spacing(bigMargins ? Spacing.Large : Spacing.None)};
  border-radius: ${({ theme }) => theme.borderRadii.outer};
  color: ${({ theme, kind }) =>
    kind === 'dark'
      ? theme.palette.text.secondaryInverted
      : theme.palette.text.secondary};

  &:hover {
    background-color: ${({ theme, kind }) =>
      kind === 'dark' ? theme.palette.neutral[800] : theme.palette.paper[400]};
  }
  width: 100%;
`;

export const StyledTopLevelNavLink = styled(NavLink)`
  display: block;
  width: 100%;
  text-decoration: none;
  color: inherit;
`;

export const GroupHeader = styled('button')`
  all: unset;
  box-sizing: border-box;
  display: block;
  width: 100%;
  margin: 0 0 ${({ theme }) => theme.spacing(Spacing.Small)};
  border-radius: ${({ theme }) => theme.borderRadii.outer};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.palette.neutral[800]};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.text.primaryInverted};
    outline-offset: -2px;
  }
`;

export const GroupChevron = styled('span', {
  shouldForwardProp: (prop) => prop !== 'expanded' && prop !== 'open',
})<{ expanded: boolean; open: boolean }>`
  display: flex;
  flex-shrink: 0;
  line-height: 0;
  transform: rotate(${({ open }) => (open ? '180deg' : '0deg')});
  ${({ expanded }) =>
    expanded
      ? `
    transition:
      transform ${SIDE_NAV_CONTENT_FADE_DURATION_MS}ms ${SIDE_NAV_EASING},
      opacity ${SIDE_NAV_CONTENT_FADE_DURATION_MS}ms ${SIDE_NAV_EASING} ${SIDE_NAV_CONTENT_FADE_DELAY_MS}ms;
    opacity: 1;
    visibility: visible;
  `
      : `
    transition:
      transform ${SIDE_NAV_CONTENT_FADE_DURATION_MS}ms ${SIDE_NAV_EASING},
      opacity ${SIDE_NAV_CONTENT_FADE_DURATION_MS}ms ${SIDE_NAV_EASING},
      visibility 0s linear ${SIDE_NAV_CONTENT_FADE_DURATION_MS}ms;
    opacity: 0;
    visibility: hidden;
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const SubmenuCollapse = styled('div', {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>`
  display: grid;
  ${({ theme, expanded }) =>
    expanded
      ? `
    grid-template-rows: 1fr;
    margin-bottom: ${theme.spacing(Spacing.Small)};
    opacity: 1;
    visibility: visible;
    transition: opacity ${SIDE_NAV_EXPAND_DURATION_MS}ms ${SIDE_NAV_EASING};

    & a {
      opacity: 1;
      transition: opacity ${SIDE_NAV_CONTENT_FADE_DURATION_MS}ms ${SIDE_NAV_EASING} ${SIDE_NAV_CONTENT_FADE_DELAY_MS}ms;
    }
  `
      : `
    grid-template-rows: 0fr;
    margin-bottom: 0;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition:
      opacity ${SIDE_NAV_CONTENT_FADE_DURATION_MS}ms ${SIDE_NAV_EASING},
      visibility 0s linear ${SIDE_NAV_CONTENT_FADE_DURATION_MS}ms,
      grid-template-rows 0s linear ${SIDE_NAV_CONTENT_FADE_DURATION_MS}ms,
      margin-bottom 0s linear ${SIDE_NAV_CONTENT_FADE_DURATION_MS}ms;

    & a {
      opacity: 0;
      transition: opacity ${SIDE_NAV_CONTENT_FADE_DURATION_MS}ms ${SIDE_NAV_EASING};
    }
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    & a {
      transition: none;
    }
  }
`;

export const SubmenuContainer = styled('div')`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadii.outer};
  background-color: ${({ theme }) => theme.palette.neutral[900]};
`;

export const SubmenuLink = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(Spacing.Small)};
  padding: ${({ theme }) => theme.spacing(Spacing.Small)};
  border-radius: ${({ theme }) => theme.borderRadii.outer};
  color: ${({ theme }) => theme.palette.text.secondaryInverted};
  text-decoration: none;

  &.active {
    color: ${({ theme }) => theme.palette.paper[200]};
  }

  &:hover {
    background-color: ${({ theme }) => theme.palette.neutral[800]};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.text.primaryInverted};
    outline-offset: -2px;
  }
`;
